/**
 * WebPlacement dialog selectors — testid registry.
 *
 * Single source of truth for ALL wp-* data-testid values
 * used in WebPlacement dialog E2E test helpers.
 */

export const WP = {
  dialog: 'wp-dialog',

  // ── Post-publish version prompt (Spec A / D51, A4-2) ──
  // Offered AFTER a successful publish: branch a new version so the just-published
  // (frozen) version stays unchanged.
  postPublishVersionDialog: 'post-publish-version-dialog',
  postPublishVersionCreate: 'post-publish-version-create',
  postPublishVersionDismiss: 'post-publish-version-dismiss',
  postPublishVersionError: 'post-publish-version-error',
  /** Strategy axis: shown only when the linked strategy is an unbaked draft. */
  postPublishBakeOffer: 'post-publish-bake-offer',
  postPublishBakeStrategy: 'post-publish-bake-strategy',
  postPublishBakeDone: 'post-publish-bake-done',

  // ── Basic Fields ──
  nameInput: 'wp-name-input',
  aliasInput: 'wp-alias-input',
  generateAliasBtn: 'wp-generate-alias-btn',

  // ── URL Parameters ──
  urlAddBtn: 'wp-url-add-btn',
  urlParamInput: 'wp-url-param-input',
  urlKeyInput: 'wp-url-key-input',
  urlSaveBtn: 'wp-url-save-btn',

  // ── Server Data ──
  serverAddBtn: 'wp-server-add-btn',
  serverPathInput: 'wp-server-path-input',
  serverKeyInput: 'wp-server-key-input',
  serverSaveBtn: 'wp-server-save-btn',

  // ── Variable Inputs (V2 only) ──
  variableAddBtn: 'wp-variable-add-btn',
  variableSaveBtn: 'wp-variable-save-btn',

  // ── Screen Inputs (V1 only) ──
  screenAddBtn: 'wp-screen-add-btn',
  screenSourceSelect: 'wp-screen-source-select',
  screenKeyInput: 'wp-screen-key-input',
  screenSaveBtn: 'wp-screen-save-btn',

  // ── Redirect Parameters ──
  redirectAddBtn: 'wp-redirect-add-btn',

  // ── Stripe Metadata ──
  stripeMetaAddBtn: 'wp-stripe-meta-add-btn',

  // ── Stripe Email ──
  stripeEmailSelect: 'wp-stripe-email-select',
  /** V1-only: screen-based Stripe email */
  stripeEmailScreenSelect: 'wp-stripe-email-screen-select',

  // ── Destination Parameter Rows ──
  /** Destination param source type: append `-{rowIndex}` */
  destParamSourceTypePrefix: 'wp-dest-param-source-type-',

  // ── Variable Bindings (V2 only) ──
  varBindingSourceType: 'wp-var-binding-source-type',
  varBindingTargetVar: 'wp-var-binding-target-var',
  varBindingSaveBtn: 'wp-var-binding-save-btn',

  // ── Webhooks ──
  webhookAddBtn: 'wp-webhook-add-btn',

  // ── Entry Screen Rules (V2 only) ──
  entryRuleAddBtn: 'wp-entry-rule-add-btn',
  entryRuleParamSelect: 'wp-entry-rule-param-select',
  entryRuleValueInput: 'wp-entry-rule-value-input',
  entryRuleScreenSelect: 'wp-entry-rule-screen-select',
  entryRuleSaveBtn: 'wp-entry-rule-save-btn',
  entryRuleCancelBtn: 'wp-entry-rule-cancel-btn',
  /** Entry screen rule row: append `-{rowIndex}` */
  entryRuleRowPrefix: 'wp-entry-rule-row-',
  /** Entry screen rule delete: append `-{rowIndex}` */
  entryRuleDeletePrefix: 'wp-entry-rule-delete-',
  /** Entry screen rule toggle: append `-{rowIndex}` */
  entryRuleTogglePrefix: 'wp-entry-rule-toggle-',

  // ── Sections (accordion headers) ──
  section: {
    urlParams: 'wp-section-url-params',
    serverData: 'wp-section-server-data',
    screenInputs: 'wp-section-screen-inputs',
    variableInputs: 'wp-section-variable-inputs',
    v2Variables: 'wp-section-v2-variables',
    redirectParams: 'wp-section-redirect-params',
    stripeMetadata: 'wp-section-stripe-metadata',
    stripeEmail: 'wp-section-stripe-email',
    webhooks: 'wp-section-webhooks',
    variableBindings: 'wp-section-variable-bindings',
    entryScreenRules: 'wp-accordion-entry-screen-rules',
    entryScreenRulesBlock: 'wp-section-entry-screen-rules',
    customVariables: 'wp-section-custom-variables',
    seo: 'wp-section-seo',
    consent: 'wp-section-consent',
  },

  // ── Switches / Toggles ──
  rememberPaymentMethodSwitch: 'wp-remember-payment-method-switch',

  // ── Dialog Actions ──
  saveOnlyBtn: 'wp-dialog-save-only-btn',
  saveBtn: 'wp-dialog-save-btn',

  // ── URL parameter rows / variable bindings (P1 ids already in components) ──
  urlCancelBtn: 'wp-url-cancel-btn',
  urlRow: (index: number) => `wp-url-row-${index}`,
  /** URL parameter row: append `{index}` — for `[data-testid^=]` row-count locators. */
  urlRowPrefix: 'wp-url-row-',
  urlDelete: (index: number) => `wp-url-delete-${index}`,
  variableSelect: 'wp-variable-select',
  varBindingAddBtn: 'wp-var-binding-add-btn',

  // ── Server data rows (UrlParametersSection.tsx sibling: ServerDataSection.tsx) ──
  serverRow: (index: number) => `wp-server-row-${index}`,
  /** Server data row: append `{index}` — for `[data-testid^=]` row-count locators. */
  serverRowPrefix: 'wp-server-row-',
  serverDelete: (index: number) => `wp-server-delete-${index}`,
  serverCancelBtn: 'wp-server-cancel-btn',

  // ── Deploy dialog (DeployWebPlacementDialog) — PRE-004 ──
  deployDialog: 'wp-deploy-dialog',
  deployDoneBtn: 'wp-deploy-done-btn',
  deployRetryBtn: 'wp-deploy-retry-btn',
  deployPublishedUrlLink: 'wp-deploy-published-url-link',
  deployCopyUrlBtn: 'wp-deploy-copy-url-btn',

  // ── Attribution toggles (Plan C, contracts §7) — PRE-010; Task 15 section wrapper ──
  attributionSection: 'wp-attribution-section',
  attributionToggleFacebook: 'wp-attribution-toggle-facebook',
  attributionToggleTiktok: 'wp-attribution-toggle-tiktok',
  attributionToggleUtm: 'wp-attribution-toggle-utm',
} as const;
